import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import { CollectionActivityType, ContactedVia, ICreateCollectionActivity } from 'src/types';
import { useLocales } from 'src/locales';

type Props = {
  open: boolean;
  clientId: number | null;
  activityType: Exclude<CollectionActivityType, 'status_change'> | null;
  onClose: () => void;
  onSubmit: (payload: ICreateCollectionActivity) => Promise<void>;
};

const CONTACT_CHANNELS: ContactedVia[] = ['whatsapp', 'phone', 'email', 'other'];

/**
 * The backend refuses a promise with no date — a promise for never is a misleading permanent
 * entry in a log whose only value is that it says what happened. Rather than let the user
 * discover that as a 400, the submit button stays disabled until the date is there.
 */
export default function CollectionActivityDialog({
  open,
  clientId,
  activityType,
  onClose,
  onSubmit,
}: Props) {
  const { t } = useLocales('operativaDashboard');
  const [note, setNote] = useState('');
  const [promiseDate, setPromiseDate] = useState('');
  const [contactedVia, setContactedVia] = useState<ContactedVia>('whatsapp');
  const [saving, setSaving] = useState(false);

  const isPromise = activityType === 'promise';
  const isContact = activityType === 'contacted';
  const canSubmit = clientId !== null && (!isPromise || promiseDate !== '') && !saving;

  const handleClose = () => {
    setNote('');
    setPromiseDate('');
    setContactedVia('whatsapp');
    onClose();
  };

  const handleSubmit = async () => {
    if (clientId === null || activityType === null) return;
    setSaving(true);
    try {
      await onSubmit({
        clientId,
        activityType,
        note: note.trim() === '' ? null : note.trim(),
        promiseDate: isPromise ? promiseDate : null,
        contactedVia: isContact ? contactedVia : null,
      });
      handleClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>{activityType ? t(`azione_${activityType}`) : ''}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {isPromise ? (
            <TextField
              required
              type="date"
              label={t('data_promessa')}
              value={promiseDate}
              onChange={(e) => setPromiseDate(e.target.value)}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          ) : null}
          {isContact ? (
            <TextField
              select
              label={t('canale')}
              value={contactedVia}
              onChange={(e) => setContactedVia(e.target.value as ContactedVia)}
            >
              {CONTACT_CHANNELS.map((channel) => (
                <MenuItem key={channel} value={channel}>
                  {t(`canale_${channel}`)}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <TextField
            multiline
            minRows={3}
            label={t('nota')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            slotProps={{
              htmlInput: { maxLength: 2000 },
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={handleClose}>
          {t('annulla')}
        </Button>
        <Button variant="contained" disabled={!canSubmit} onClick={handleSubmit}>
          {t('salva')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
